import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { OccEndpointsService, TranslationService } from '@spartacus/core';
import { Observable, catchError, map } from 'rxjs';
import { ShipmentsRequestConfig, ShipmentsStatusRequest, Shipment } from '../../../../core/models/shipments.model';
import { failStatus, successStatus } from '../../../../core/models/request-status.model';

@Injectable({
  providedIn: 'root',
})
export class ShipmentsAdapter {
  constructor(
    private http: HttpClient,
    private translationService: TranslationService,
    private occEndpointsService: OccEndpointsService,
  ) {}

  getShipments(shipmentsRequestConfig: ShipmentsRequestConfig): Observable<ShipmentsStatusRequest> {
    const url = this.occEndpointsService.buildUrl('getShipments', {
      queryParams: {
        fromDate: shipmentsRequestConfig.fromDate,
        toDate: shipmentsRequestConfig.toDate,
        openOnly: shipmentsRequestConfig.openOnly,
        delayedOnly: shipmentsRequestConfig.delayedOnly,
        poNumber: shipmentsRequestConfig.poNumber,
        invoiceNumber: shipmentsRequestConfig.invoiceNumber,
        orderNumber: shipmentsRequestConfig.orderNumber,
        partNumber: shipmentsRequestConfig.partNumber
      },
    });

    return this.http.get(url).pipe(
      map((response: any) => ({
        shipments: this.normalizeShipments(response),
        ...successStatus,
      })),
      catchError(() =>
        this.translationService.translate('generic.error').pipe(
          map(textError => ({
            ...failStatus,
            message: textError,
          })),
        ),
      ),
    );
  }

  getShipmentsExport(shipmentsRequestConfig: ShipmentsRequestConfig): Observable<any> {

    const url = this.occEndpointsService.buildUrl('getShipmentsExport', {
      queryParams: {
        fromDate: shipmentsRequestConfig.fromDate,
        toDate: shipmentsRequestConfig.toDate,
        openOnly: shipmentsRequestConfig.openOnly,
        delayedOnly: shipmentsRequestConfig.delayedOnly,
        poNumber: shipmentsRequestConfig.poNumber,
        invoiceNumber: shipmentsRequestConfig.invoiceNumber,
        orderNumber: shipmentsRequestConfig.orderNumber,
        partNumber: shipmentsRequestConfig.partNumber
      },
    });

    return this.http.get(url, { responseType: 'json' });
  }

  private normalizeShipments(response: any): Shipment[] {
    const shipments: Shipment[] = [];
    const urlRegex = /<A HREF="([^"]+)"/i;

    (response || []).forEach((shipmentData: any) => {
      shipments.push({
        orderUid: shipmentData?.orderUid,
        orderId: shipmentData.orderId,
        orderNumber: shipmentData.orderNumber,
        orderDate: shipmentData?.orderDate ? new Date(shipmentData?.orderDate) : undefined,
        orderStatus: shipmentData.orderStatus,
        poNumber: shipmentData.poNumber,
        shipDate: shipmentData?.shipDate ? new Date(shipmentData?.shipDate) : undefined,
        revisedShipDate: shipmentData?.revisedShipDate ? new Date(shipmentData?.revisedShipDate) : undefined,
        actualShipDate: shipmentData?.actualShipDate ? new Date(shipmentData?.actualShipDate) : undefined,
        parentOrderId: shipmentData.parentOrderId,
        parentOrderNumber: shipmentData.parentOrderNumber,
        relatedOrderId: shipmentData.relatedOrderId,
        relatedOrderNumber: shipmentData.relatedOrderNumber,
        trackingNumber: shipmentData.trackingNumber,
        trackingInfo: (() => {
          const match = urlRegex.exec(shipmentData.trackingInfo);
          return match ? match[1] : undefined;
      })(),
        items: (shipmentData.items || []).map((itemData: any) => ({
          itemId: itemData.itemId,
          partNumber: itemData.partNumber,
          catalogNumber: itemData.catalogNumber,
          customerPartNumber: itemData.customerPartNumber,
          quantityOrdered: itemData.quantityOrdered,
          quantityShipped: itemData.quantityShipped,
          quantityCancelled: itemData.quantityCancelled,
          invoiceNumber: itemData.invoiceNumber,
					invoiceUid: itemData.invoiceUid,
          itemTypeCode: itemData.itemTypeCode,
        })),
      });
    });

    return shipments;
  }
}
